// 1 Hz telemetry recorder. Pulls live snapshots from the active FTMS device
// and the global HR monitor (both held by ConnectedEquipmentContext),
// builds compact per-second rows, and flushes them to IndexedDB every 5 s
// so a tab crash mid-workout doesn't lose the data.
//
// Three design constraints that drive the implementation:
//
//   1. setInterval drifts under background-tab throttling — Chrome clamps
//      to ~1/s minimum when foregrounded, ~1/10s when backgrounded, and
//      iOS Safari may suspend the JS context entirely. We base `t` on
//      performance.now() and BACKFILL any seconds the interval skipped,
//      so the time axis stays wall-clock-correct.
//
//   2. BLE refs retain the last known value indefinitely once notifications
//      stop. Without a watchdog, a 5-minute PM5 sleep would land 300 rows
//      of zombie 200 W into the timeline. We read via getFreshSnapshot()
//      which returns null past a 3 s staleness threshold.
//
//   3. We must not delete the IDB entry until after a successful save —
//      a network failure mid-save should leave the data recoverable on
//      the next app load.
//
//   4. One workout is one recording. A recorder that unmounts mid-session
//      (the live Coach when you open another tab) must pick its session up
//      again on return, not start a second one: the first would be left in
//      IDB, never cleared by the save, and come back as an "interrupted
//      workout" that duplicates the saved one. `persistKey` keeps the
//      session in memory across the remount.
//
//   5. In the Android app a foreground service keeps its own record of the
//      strap (platform/backgroundHr.js), so seconds this page missed while
//      Android had it stopped (a video call, say) get their heart rate back
//      instead of staying a gap.

import { useCallback, useEffect, useRef } from "react";
import { useConnectedEquipment } from "../contexts/ConnectedEquipmentContext";
import { idbDelete, idbPut } from "../utils/telemetry/idb";
import { summariseTelemetry } from "../utils/telemetry/summary";
import { isAirBike } from "../utils/airBike";
import { zoneFloors } from "../utils/heartRateZones";
import {
  backgroundHrAvailable,
  fillBikeFromBackground,
  fillHrFromBackground,
  refreshBackgroundHr,
  resetBackgroundHr,
  startBackgroundHr,
  noteBackgroundHr,
  stopBackgroundHr,
} from "../platform/backgroundHr";

const TICK_MS = 1000;
const IDB_FLUSH_MS = 5000;
const STALENESS_MS = 3000;
// Rows before this tick's that are re-checked against the background samples.
const RECHECK_ROWS = 5;

// Sessions of recorders that unmounted mid-workout, by persistKey.
const parked = new Map();

const makeSessionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const buildRowFromContext = (t, equipment, force) => {
  const row = { t };
  if (force) return row;

  const active = equipment.active;
  const fmt = active?.getFreshSnapshot?.(STALENESS_MS);
  if (active && fmt) {
    row.dev = active.type;
    if (fmt.power_watts != null) row.w = Math.round(fmt.power_watts);
    if (active.type === "indoor_bike") {
      // Air bikes are left out of the VO2max estimate (utils/vo2max).
      if (isAirBike(active)) row.air = 1;
      if (fmt.cadence_rpm != null) row.c = Math.round(fmt.cadence_rpm * 10) / 10;
      if (fmt.speed_kmh != null) row.sp = Math.round(fmt.speed_kmh * 10);
    } else if (active.type === "rower") {
      if (fmt.stroke_rate_spm != null) {
        row.spm = Math.round(fmt.stroke_rate_spm * 10) / 10;
      }
      if (fmt.pace_sec_per_500m != null) row.p = fmt.pace_sec_per_500m;
      if (fmt.stroke_count != null) row.sc = fmt.stroke_count;
    }
    if (fmt.resistance_level != null) row.r = fmt.resistance_level;
    if (fmt.total_distance_m != null) row.d = fmt.total_distance_m;
    // The machine's own calorie count, so the app can match its console.
    if (fmt.total_energy_kcal != null) row.e = fmt.total_energy_kcal;
  }

  const hrSnap = equipment.hr?.getFreshSnapshot?.(STALENESS_MS);
  if (hrSnap?.bpm != null) row.hr = hrSnap.bpm;
  // No strap: the heart rate the machine passes on (a bike or rower paired to
  // a chest strap or holding grip sensors) is better than none.
  else if (fmt?.heart_rate_bpm > 0) row.hr = fmt.heart_rate_bpm;

  return row;
};

/**
 * @param {Object} opts
 * @param {boolean} opts.active - true while a workout is actively recording
 * @param {string} [opts.persistKey] - names the workout, so a remount of the
 *   recorder carries on its session rather than starting another
 */
export default function useTelemetryRecorder({ active, hrBasis = null, persistKey = null }) {
  // Read at finalize, so a Profile change mid-session uses the latest.
  const hrBasisRef = useRef(hrBasis);
  hrBasisRef.current = hrBasis;
  const equipment = useConnectedEquipment();

  const samplesRef = useRef([]);
  const summaryRef = useRef(null);
  const sessionIdRef = useRef(null);
  const startedAtMsRef = useRef(0);
  const startedAtWallRef = useRef(0);
  const nextTRef = useRef(0);
  // The step the Coach moved to, waiting for the row it lands on (#200).
  const pendingMarkRef = useRef(null);
  const tickTimerRef = useRef(null);
  const idbTimerRef = useRef(null);
  const equipmentRef = useRef(equipment);
  equipmentRef.current = equipment;

  // Pick up a session this workout's recorder parked when it unmounted.
  if (persistKey && !sessionIdRef.current && parked.has(persistKey)) {
    const p = parked.get(persistKey);
    parked.delete(persistKey);
    sessionIdRef.current = p.sessionId;
    startedAtMsRef.current = p.startedAtMs;
    startedAtWallRef.current = p.startedAtWall;
    samplesRef.current = p.samples;
    nextTRef.current = p.nextT;
    pendingMarkRef.current = p.pendingMark;
  }
  const persistKeyRef = useRef(persistKey);
  persistKeyRef.current = persistKey;
  useEffect(
    () => () => {
      const key = persistKeyRef.current;
      if (!key || !sessionIdRef.current) return;
      parked.set(key, {
        sessionId: sessionIdRef.current,
        startedAtMs: startedAtMsRef.current,
        startedAtWall: startedAtWallRef.current,
        samples: samplesRef.current,
        nextT: nextTRef.current,
        pendingMark: pendingMarkRef.current,
      });
    },
    [],
  );

  const ensureSession = useCallback(() => {
    if (sessionIdRef.current) return;
    sessionIdRef.current = makeSessionId();
    startedAtMsRef.current = performance.now();
    startedAtWallRef.current = Date.now();
    samplesRef.current = [];
    summaryRef.current = null;
    nextTRef.current = 0;
    resetBackgroundHr();
  }, []);

  // Whether the bike the service records is an air bike (rows carry `air`).
  const bikeAirRef = useRef(false);

  // Wall-clock ms of a row's second, for matching the background samples.
  const wallAt = useCallback((t) => startedAtWallRef.current + t * 1000, []);

  // Stamps a pending step mark on the first row at or after its second.
  const pushRow = useCallback((row) => {
    const mark = pendingMarkRef.current;
    if (mark && row.t >= mark.t) {
      row.ph = mark.ph;
      pendingMarkRef.current = null;
    }
    samplesRef.current.push(row);
  }, []);

  const tick = useCallback(() => {
    if (!sessionIdRef.current) return;
    const eq = equipmentRef.current;
    const tNow = Math.floor(
      (performance.now() - startedAtMsRef.current) / 1000,
    );
    // This second already has its row: the interval and the visibility
    // re-tick can land in the same second, which wrote t twice (3 Nov 2026).
    if (tNow < nextTRef.current) return;
    // Backfill any seconds the interval skipped (background throttle, OS
    // suspend, etc.). Backfilled rows are { t } only — every metric absent,
    // which the chart renders as an honest gap.
    const from = samplesRef.current.length;
    while (nextTRef.current < tNow) {
      pushRow(buildRowFromContext(nextTRef.current, eq, /* force */ true));
      nextTRef.current += 1;
    }
    pushRow(buildRowFromContext(tNow, eq, false));
    nextTRef.current = tNow + 1;
    // ...unless the Android app's service heard the strap in those seconds.
    // A few rows back too: a packet can reach the service just after the
    // tick that wanted it. finalize() fills the rest.
    if (backgroundHrAvailable()) {
      const recent = samplesRef.current.slice(Math.max(0, from - RECHECK_ROWS));
      fillHrFromBackground(recent, wallAt);
      fillBikeFromBackground(recent, wallAt, { air: bikeAirRef.current });
    }
  }, [pushRow, wallAt]);

  // In the Android app, pull the service's samples first so a tick that
  // closes a gap can fill it. Elsewhere this is just tick().
  const tickWithBackground = useCallback(() => {
    if (!backgroundHrAvailable() || !sessionIdRef.current) {
      tick();
      return;
    }
    refreshBackgroundHr().then(tick, tick);
  }, [tick]);

  /**
   * Note that the session moved to a new step, so the chart can lay the
   * steps along the trace (#200). Before recording starts the mark waits for
   * the first row.
   */
  const mark = useCallback((ph) => {
    const t = sessionIdRef.current
      ? Math.floor((performance.now() - startedAtMsRef.current) / 1000)
      : 0;
    // This second's row may already be in (the first tick runs before the
    // first mark): stamp it rather than the next one.
    const rows = samplesRef.current;
    const last = rows[rows.length - 1];
    if (sessionIdRef.current && last && last.t === t && !last.ph) {
      last.ph = ph;
      pendingMarkRef.current = null;
      return;
    }
    pendingMarkRef.current = { t, ph };
  }, []);

  const flushToIdb = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    await idbPut(sessionId, {
      startedAtMs: startedAtMsRef.current,
      startedAt: new Date(startedAtWallRef.current).toISOString(),
      // Wall-clock epoch ms of this flush — the recovery banner's idle signal
      // (distinct from startedAtMs, which is a monotonic performance.now()).
      // It freezes at the last flush before the tab is suspended/killed, so
      // `now - updatedAt` is the true time since the recorder last ran.
      updatedAt: Date.now(),
      sampleCount: samplesRef.current.length,
      samples: samplesRef.current,
    });
  }, []);

  // Start / stop the 1 Hz tick + 5 s IDB flush as `active` flips.
  useEffect(() => {
    if (!active) return undefined;

    ensureSession();
    // First tick immediately so t=0 has a row.
    tick();

    tickTimerRef.current = setInterval(tickWithBackground, TICK_MS);
    idbTimerRef.current = setInterval(flushToIdb, IDB_FLUSH_MS);

    // Re-tick on visibility change to close throttle gaps the moment the
    // tab is foregrounded again.
    const onVisibility = () => {
      if (document.visibilityState === "visible") tickWithBackground();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
      if (idbTimerRef.current) clearInterval(idbTimerRef.current);
      tickTimerRef.current = null;
      idbTimerRef.current = null;
      document.removeEventListener("visibilitychange", onVisibility);
      // Final flush so what we have is recoverable even if `active` toggles
      // off before finalize() runs.
      flushToIdb();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Android app: keep the strap recording in the background while this
  // workout records. Started (or moved) whenever the strap changes; stopped
  // by finalize / discard / clearAfterSave / release, not on unmount, since a
  // parked recorder's workout is still going.
  // The bike too (FTMS indoor bike only), which also lets the service keep the
  // timer fed while Android has this page stopped.
  const hrId = equipment.hr?.id || null;
  const hrName = equipment.hr?.name || "";
  const bikeDevice = equipment.active?.type === "indoor_bike" ? equipment.active : null;
  const bikeId = bikeDevice?.id || null;
  bikeAirRef.current = bikeDevice ? isAirBike(bikeDevice) : false;
  useEffect(() => {
    if (!active || !backgroundHrAvailable()) return;
    if (!hrId && !bikeId) {
      noteBackgroundHr("recording without a strap or bike: nothing to record in the background");
      return;
    }
    noteBackgroundHr(
      `recording with ${[hrId && (hrName || "a strap"), bikeId && "a bike"].filter(Boolean).join(" and ")}`,
    );
    let zones = null;
    try {
      zones = hrBasisRef.current ? zoneFloors(hrBasisRef.current) : null;
    } catch {
      zones = null;
    }
    startBackgroundHr({ deviceId: hrId, deviceName: hrName, bikeId, zones });
  }, [active, hrId, hrName, bikeId]);

  /**
   * Compute the summary, push one final flush, and return the full bundle.
   * Does NOT delete the IDB entry — that's the caller's responsibility
   * after a successful network save (so a save failure can be recovered).
   */
  const finalize = useCallback(async () => {
    if (!sessionIdRef.current) return null;
    if (backgroundHrAvailable()) {
      await stopBackgroundHr(); // pulls the last samples first
      fillHrFromBackground(samplesRef.current, wallAt);
      fillBikeFromBackground(samplesRef.current, wallAt, { air: bikeAirRef.current });
    }
    summaryRef.current = summariseTelemetry(samplesRef.current, { hrBasis: hrBasisRef.current });
    await flushToIdb();
    return {
      sessionId: sessionIdRef.current,
      startedAt: new Date(startedAtWallRef.current).toISOString(),
      samples: samplesRef.current,
      summary: summaryRef.current,
    };
  }, [flushToIdb, wallAt]);

  /**
   * Drop the in-flight session entirely (used by the recovery banner's
   * "Discard" action and by callers that abandon a workout without saving).
   */
  const discard = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    sessionIdRef.current = null;
    if (persistKeyRef.current) parked.delete(persistKeyRef.current);
    samplesRef.current = [];
    summaryRef.current = null;
    nextTRef.current = 0;
    pendingMarkRef.current = null;
    stopBackgroundHr();
    if (sessionId) await idbDelete(sessionId);
  }, []);

  /**
   * Delete the IDB entry — call after a successful network save.
   */
  const clearAfterSave = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    sessionIdRef.current = null;
    if (persistKeyRef.current) parked.delete(persistKeyRef.current);
    stopBackgroundHr();
    if (sessionId) await idbDelete(sessionId);
  }, []);

  /**
   * Let go of a workout that ended without a save, so the next one starts
   * its own recording. The IDB entry stays: what was recorded can still be
   * recovered from the banner.
   */
  const release = useCallback(() => {
    if (!sessionIdRef.current) return;
    flushToIdb();
    sessionIdRef.current = null;
    samplesRef.current = [];
    summaryRef.current = null;
    nextTRef.current = 0;
    pendingMarkRef.current = null;
    if (persistKeyRef.current) parked.delete(persistKeyRef.current);
    stopBackgroundHr();
  }, [flushToIdb]);

  return {
    samplesRef,
    summaryRef,
    sessionIdRef,
    finalize,
    discard,
    clearAfterSave,
    release,
    mark,
  };
}

/**
 * Marks each step the session reaches in the recording (#200). A step is
 * its place in the plan plus the round, so the same exercise in round two
 * is a new step. A pause is a step of its own: the recorder keeps going
 * through it (heart rate doesn't stop), and resuming marks the step again.
 */
export function useTelemetryStepMarks(mark, coachState) {
  const node = coachState?.currentNode;
  const paused = coachState?.status === "paused";
  const key = paused
    ? "paused"
    : node?.id
      ? `${(coachState.path || []).join(",")}#${JSON.stringify(coachState.loopCounters || {})}`
      : "";
  const nodeRef = useRef(node);
  nodeRef.current = node;
  useEffect(() => {
    if (key === "paused") {
      mark({ id: null, label: "Paused", type: "Pause" });
      return;
    }
    const current = nodeRef.current;
    if (!key || !current) return;
    mark({ id: current.id, label: current.label || "", type: current.type || "" });
  }, [key, mark]);
}
